/**
 * components/DataTable — a generic, horizontally-scrollable table primitive. Columns
 * declare an accessor + optional right-alignment; an empty state renders when there
 * are no rows. Kept deliberately small — richer sorting/paging lands with real pages.
 */
import type { ReactNode } from 'react';

export interface Column<Row> {
  readonly key: string;
  readonly header: ReactNode;
  readonly align?: 'left' | 'right';
  readonly cell: (row: Row) => ReactNode;
}

interface DataTableProps<Row> {
  readonly columns: readonly Column<Row>[];
  readonly rows: readonly Row[];
  readonly rowKey: (row: Row, index: number) => string | number;
  readonly empty?: ReactNode;
}

export function DataTable<Row>({ columns, rows, rowKey, empty }: DataTableProps<Row>): JSX.Element {
  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={c.align === 'right' ? 'align-right' : undefined}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td className="data-table__empty" colSpan={columns.length}>
                {empty ?? 'No rows.'}
              </td>
            </tr>
          ) : (
            rows.map((row, i) => (
              <tr key={rowKey(row, i)}>
                {columns.map((c) => (
                  <td key={c.key} className={c.align === 'right' ? 'align-right' : undefined}>
                    {c.cell(row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
